import React, { useState, useEffect } from "react";
import { Admin, Resource, CustomRoutes, radiantDarkTheme } from "react-admin";
import { BrowserRouter, Routes, Route } from "react-router";
import * as Icons from "@mui/icons-material";
import { deepmerge } from "@mui/utils";

import Pages from "./pages/index.js";
import Providers from "./providers/index.js";
import "./styles.scss";
import { Loader } from "./shared/components/index.js";
import DarkTheme from "./darkTheme.js";

const combinedDarkTheme = deepmerge(DarkTheme, radiantDarkTheme);

export const App = () => {
  if (window.location.pathname.startsWith("/admin/")) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/admin/*" element={<div>Block preview page</div>} />
        </Routes>
      </BrowserRouter>
    );
  }

  const [pluginsLoaded, setPluginsLoaded] = useState(false);
  const [plugins, setPlugins] = useState(false);
  const [currentMerchantLoaded, setCurrentMerchantLoaded] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState("uklon");
  const allowedMerchants = ["Mango", "PetrolCard"];

  const loadCurrentMerchant = (merchant) => {
    setCurrentMerchant(merchant);
  };

  window.addEventListener("login", () => {
    setPlugins(JSON.parse(localStorage.getItem("plugins")));
    loadCurrentMerchant(localStorage.getItem("name"));
  });

  useEffect(() => {
    const merchant = localStorage.getItem("name");

    if (!plugins) {
      setPlugins(JSON.parse(localStorage.getItem("plugins")));
    }
    setPluginsLoaded(true);

    if (merchant) {
      loadCurrentMerchant(merchant);
    }

    setCurrentMerchantLoaded(true);
  }, []);

  if (!pluginsLoaded) return <Loader />;
  if (!currentMerchantLoaded) return <Loader />;

  return (
    <Admin
      authProvider={Providers.authProvider}
      dataProvider={Providers.dataProvider()}
      i18nProvider={Providers.i18nProvider}
      dashboard={Pages.Dashboard}
      disableTelemetry
      defaultTheme="light"
      darkTheme={combinedDarkTheme}
    >
      <CustomRoutes>
        <Route path="/error_page" element={<Pages.ErrorPage />} />
        <Route
          path="/PushNotificationService/:id/send"
          element={<Pages.PushNotificationService.SendNotification />}
        />
      </CustomRoutes>
      <Resource
        name="Merchant"
        list={Pages.Merchant.List}
        show={Pages.Merchant.Show}
        edit={Pages.Merchant.Edit}
        icon={Icons.AccountBox}
      />
      {plugins && plugins.clients && (
        <Resource
          name="Client"
          list={Pages.Client.List}
          show={Pages.Client.Show}
          edit={Pages.Client.Edit}
          create={Pages.Client.Create}
          icon={Icons.People}
        />
      )}
      {plugins && plugins.brandMerchants && (
        <Resource
          name="BrandMerchant"
          list={Pages.BrandMerchant.List}
          show={Pages.BrandMerchant.Show}
          edit={Pages.BrandMerchant.Edit}
          create={Pages.BrandMerchant.Create}
          icon={Icons.Groups}
        />
      )}
      {plugins && plugins.gasBrandMerchants && (
        <Resource
          name="GasBrandMerchant"
          list={Pages.GasBrandMerchant.List}
          create={Pages.GasBrandMerchant.Create}
          show={Pages.GasBrandMerchant.Show}
          edit={Pages.GasBrandMerchant.Edit}
          icon={Icons.LocalGasStationTwoTone}
        />
      )}
      {plugins && plugins.merchantPaymentGateways && (
        <Resource
          name="MerchantPaymentGateway"
          list={Pages.MerchantPaymentGateway.List}
          show={Pages.MerchantPaymentGateway.Show}
          edit={Pages.MerchantPaymentGateway.Edit}
          create={Pages.MerchantPaymentGateway.Create}
          icon={Icons.AccountBalance}
        />
      )}
      {plugins && plugins.qrCards && plugins.datexPayment && (
        <Resource
          name="QrCard"
          list={Pages.QrCard.List}
          show={Pages.QrCard.Show}
          create={Pages.QrCard.Create}
          icon={Icons.CreditCard}
        />
      )}
      {plugins && plugins.coupons && plugins.datexCoupon && (
        <Resource
          name="Coupon"
          create={Pages.Coupon.Create}
          list={Pages.Coupon.List}
          edit={Pages.Coupon.Edit}
          show={Pages.Coupon.Show}
          icon={Icons.GasMeter}
        />
      )}
      {plugins && plugins.transactions && (
        <Resource
          name="DatexTransaction"
          list={Pages.Transaction.List}
          show={plugins.datexPayment && <Pages.Transaction.Show />}
          icon={Icons.Receipt}
        />
      )}
      {plugins && plugins.truncs && (
        <Resource
          name="Trunc"
          list={
            allowedMerchants.includes(currentMerchant) ? (
              <Pages.Trunc.List isCreateAllowed={true} />
            ) : (
              <Pages.Trunc.List isCreateAllowed={false} />
            )
          }
          show={Pages.Trunc.Show}
          create={Pages.Trunc.Create}
          icon={Icons.AccountBalanceWallet}
        />
      )}
      {plugins && plugins.files && (
        <Resource
          name="File"
          list={Pages.File.List}
          show={Pages.File.Show}
          create={Pages.File.Create}
          icon={Icons.PictureAsPdf}
        />
      )}
      {plugins && plugins.smsServices && (
        <Resource
          name="SmsService"
          create={Pages.SmsService.Create}
          list={Pages.SmsService.List}
          edit={Pages.SmsService.Edit}
          icon={Icons.Sms}
        />
      )}
      {plugins && plugins.emailServices && (
        <Resource
          name="EmailService"
          list={Pages.EmailService.List}
          create={Pages.EmailService.Create}
          edit={Pages.EmailService.Edit}
          icon={Icons.AlternateEmail}
        />
      )}
      {plugins && plugins.pushNotificationServices && (
        <Resource
          name="PushNotificationService"
          edit={Pages.PushNotificationService.Edit}
          list={Pages.PushNotificationService.List}
          create={Pages.PushNotificationService.Create}
        />
      )}
      {plugins && plugins.promotions && (
        <Resource
          name="Promotion"
          list={Pages.Promotion.List}
          show={Pages.Promotion.Show}
          edit={Pages.Promotion.Edit}
          create={Pages.Promotion.Create}
          icon={Icons.NotificationsActive}
        />
      )}
      {plugins && plugins.designEditor && (
        <Resource
          name="Design"
          list={Pages.Design.List}
          show={Pages.Design.Show}
          edit={Pages.Design.Edit}
          create={Pages.Design.Create}
          icon={Icons.Brush}
        />
      )}
      {plugins && plugins.pageEditor && (
        <Resource
          name="Page"
          create={Pages.Page.Create}
          show={Pages.Page.Show}
          list={Pages.Page.List}
          edit={Pages.Page.Edit}
          icon={Icons.Description}
        />
      )}
      {plugins && plugins.blocksEditor && (
        <Resource
          name="Block"
          list={Pages.Block.List}
          show={Pages.Block.Show}
          edit={Pages.Block.Edit}
          create={Pages.Block.Create}
          icon={Icons.Widgets}
        />
      )}
      {plugins && plugins.categoriesEditor && (
        <Resource
          name="Category"
          list={Pages.Category.List}
          show={Pages.Category.Show}
          edit={Pages.Category.Edit}
          create={Pages.Category.Create}
          icon={Icons.Class}
        />
      )}
      {plugins && plugins.tagsEditor && (
        <Resource
          name="Tag"
          list={Pages.Tag.List}
          show={Pages.Tag.Show}
          edit={Pages.Tag.Edit}
          create={Pages.Tag.Create}
          icon={Icons.Tag}
        />
      )}
      {plugins && plugins.support && (
        <Resource
          name="Chat"
          list={Pages.Chat.List}
          icon={Icons.SupportAgent}
        />
      )}
      {plugins && plugins.asyncJobs && (
        <Resource
          name="AsyncJob"
          list={Pages.AsyncJob.List}
          icon={Icons.Task}
        />
      )}
      {plugins && plugins.totals && plugins.datexPayment && (
        <Resource
          name="Total"
          list={Pages.Total.List}
          icon={Icons.QueryStats}
        />
      )}
    </Admin>
  );
};
